/**
 * useImagePiiExtract — Extract PII from camera photos via GraphQL → Presidio
 *
 * Routes through GraphQL gateway for scope protection (pii:capture).
 * Gateway calls Presidio for OCR + PII detection.
 * Returns extracted fields with tokens for vault storage.
 *
 * DRR-302: PII never goes to Claude — Presidio runs in isolated service.
 *
 * Usage:
 *   const { extractPii, isExtracting, result, error } = useImagePiiExtract();
 *   await extractPii(imageBlob, { subjectId: '...' });
 *   // result.extractedFields contains PII with tokens
 */

import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

const EXTRACT_IMAGE_PII_MUTATION = gql`
  mutation ExtractImagePii($input: ExtractImagePiiInput!) {
    extractImagePii(input: $input) {
      success
      documentType
      extractedFields {
        entityType
        maskedValue
        token
        confidence
      }
      mappingId
      ocrTextMasked
      processingTimeMs
      error
    }
  }
`;

export interface ExtractedPiiField {
  entityType: string;
  maskedValue: string;
  token: string | null;
  confidence: number;
}

export interface ImagePiiResult {
  documentType: string | null;
  extractedFields: ExtractedPiiField[];
  mappingId: string | null;
  ocrTextMasked: string;
  processingTimeMs: number;
}

export interface ExtractPiiOptions {
  subjectId?: string;
  documentId?: string;
  storeInVault?: boolean;
  language?: string;
}

interface UseImagePiiExtractReturn {
  extractPii: (imageBlob: Blob, options?: ExtractPiiOptions) => Promise<ImagePiiResult>;
  isExtracting: boolean;
  result: ImagePiiResult | null;
  error: Error | null;
  reset: () => void;
}

export function useImagePiiExtract(): UseImagePiiExtractReturn {
  const [result, setResult] = useState<ImagePiiResult | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const [executeMutation, { loading: isExtracting }] = useMutation(EXTRACT_IMAGE_PII_MUTATION);

  const extractPii = useCallback(
    async (imageBlob: Blob, options: ExtractPiiOptions = {}): Promise<ImagePiiResult> => {
      setError(null);

      try {
        // Convert blob to base64
        const arrayBuffer = await imageBlob.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        const { data } = await executeMutation({
          variables: {
            input: {
              imageBase64: base64,
              subjectId: options.subjectId,
              documentId: options.documentId,
              storeInVault: options.storeInVault ?? false,
              language: options.language,
            },
          },
        });

        const response = data?.extractImagePii;

        if (!response?.success) {
          throw new Error(response?.error || 'PII extraction failed');
        }

        const extractedResult: ImagePiiResult = {
          documentType: response.documentType,
          extractedFields: response.extractedFields.map((f: Record<string, unknown>) => ({
            entityType: f.entityType,
            maskedValue: f.maskedValue,
            token: f.token,
            confidence: f.confidence,
          })),
          mappingId: response.mappingId,
          ocrTextMasked: response.ocrTextMasked || '',
          processingTimeMs: response.processingTimeMs,
        };

        setResult(extractedResult);
        return extractedResult;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [executeMutation]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    extractPii,
    isExtracting,
    result,
    error,
    reset,
  };
}

export default useImagePiiExtract;
